import {
  LabeledAfbeelding,
  LabeledBevestigingInput,
  LabeledDateInput,
  LabeledJaarMaandInput,
  LabeledTextInput,
  SettingsContext
} from "adviesbox-shared";
import { connect, FormikContextType } from "formik";
import React, { ReactElement, useContext, useEffect } from "react";
import { SoortKredietProductOptions } from "../../.generated/forms/formstypes";
import LabeledHdnKeuzelijst from "../../shared/components/hdn-keuzelijst/LabeledHdnKeuzelijst";
import LabeledMaatschappijKeuzelijst from "../../shared/components/maatschappij-keuzelijst/LabeledMaatschappijKeuzelijst";
import { Soort } from "../../shared/hooks/use-maatschappij-data";
import { partijAnders, partijOnafhankelijk } from "../infra/product-constanten";
import { ProductKenmerken } from "../infra/product-kenmerken-types";
import { getProductenOverzichtTextResources } from "../infra/producten-overzicht-resources";
import { ProductDetailType, ProductenState, ProductSoort, SituatieSoort } from "../infra/producten-overzicht-types";
import { LocalDate } from "@js-joda/core";

type ProductProps = {
  selected: number;
  situatie: SituatieSoort;
  kenmerken: ProductKenmerken;
  productSoort: ProductSoort;
  isStarterslening: boolean;
};

const PRODUCTNAMEN_VOOR_AOW_EINDDATUM = [
  "arbeidsongeschiktheidsverzekering",
  "inkomensgarantieverzekering",
  "wga-gat verzekering",
  "wia-excedent verzekering"
];

function bepaalMaatschappijKeuzelijst(productSoort: ProductSoort): Soort {
  switch (productSoort) {
    case "vermogen":
      return "Vermogensbeheerders";
    case "hypotheek":
      return "Geldverstrekkers";
  }

  return "Verzekeringsmaatschappijen";
}

const Product = ({
  selected,
  situatie,
  kenmerken,
  productSoort,
  isStarterslening,
  formik: {
    setFieldValue,
    values: { producten, aanvrager1, aanvrager2 }
  }
}: ProductProps & {
  formik: FormikContextType<ProductenState>;
}): ReactElement => {
  const { productenOrigin, OcpApimSubscriptionKey } = useContext(SettingsContext);
  const product = producten[selected];
  const isDoorlopendInVoorstel = product.product.doorlopend && situatie === "voorstel";
  const isVoorstelHypotheek = productSoort === "hypotheek" && situatie === "voorstel";
  const isHuidigHypotheek = productSoort === "hypotheek" && situatie === "huidig";
  const productnummerCaption = productSoort === "hypotheek" ? "Leningnummer" : "Productnummer";
  const aow1: LocalDate | null = aanvrager1?.aowdatum ?? null;
  const aow2: LocalDate | null = aanvrager2?.aowdatum ?? null;

  const isEqual = (d1: LocalDate | null, d2: LocalDate | null): boolean => !!d1 && !!d2 && d1.equals(d2);

  useEffect(() => {
    const productNaam = product.product.productNaam;

    const isRelevantProduct =
      typeof productNaam === "string" && PRODUCTNAMEN_VOOR_AOW_EINDDATUM.includes(productNaam.toLowerCase());

    if (!isRelevantProduct) return;

    const verzekerdenKeuze = product.verzekerden?.verzekerden;
    const targetAow: LocalDate | null = verzekerdenKeuze === "Aanvrager2" ? aow2 : aow1;

    const isEinddatumNull = product.product.einddatum ? false : true;
    const equalsAow1 = isEqual(product.product.einddatum, aow1);
    const equalsAow2 = isEqual(product.product.einddatum, aow2);

    if (isEinddatumNull || equalsAow1 || equalsAow2) {
      setFieldValue(`producten[${selected}].product.einddatum`, targetAow);
    }
  }, [product, aow1, aow2, selected, setFieldValue]);

  /* istanbul ignore next */
  if (selected >= producten.length) return <></>;

  return (
    <>
      {kenmerken.productnummerTonen && (
        <LabeledTextInput caption={productnummerCaption} name={`producten[${selected}].product.productNummer`} />
      )}

      {(() => {
        if (isStarterslening) {
          return (
            <LabeledTextInput
              caption="Partij"
              name={`producten[${selected}].product.partijNaam`}
              value="Svn"
              readonly
              fieldSize="xl"
            />
          );
        } else if (productSoort === "hypotheek") {
          return (
            <LabeledMaatschappijKeuzelijst
              caption="Partij"
              name={`producten[${selected}].product.partijCodeSelectie`}
              soort={bepaalMaatschappijKeuzelijst(productSoort)}
              readonly={isVoorstelHypotheek}
              addAnders={true}
              fieldSize="xl"
              inclInactievePartijen={!isVoorstelHypotheek}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                setFieldValue(`producten[${selected}].product.partijCodeSelectie`, e.target.value);
                if (e.target.selectedIndex !== -1) {
                  setFieldValue(
                    `producten[${selected}].product.partijNaam`,
                    e.target[e.target.selectedIndex].textContent
                  );
                }
              }}
            />
          );
        } else if (productSoort !== "overige" && producten[selected].partijCode !== partijOnafhankelijk) {
          return (
            <LabeledMaatschappijKeuzelijst
              caption="Partij"
              name={
                producten[selected].product.doorlopend && producten[selected].product.partijNaam
                  ? `producten[${selected}].product.partijNaam`
                  : `producten[${selected}].partijCode`
              }
              soort={bepaalMaatschappijKeuzelijst(productSoort)}
              fieldSize="xl"
              readonly
            />
          );
        }
        return null;
      })()}
      {kenmerken.maatschappijkeuzeEnabled && (
        <>
          {productSoort !== "hypotheek" && producten[selected].partijCode === partijOnafhankelijk && (
            <LabeledHdnKeuzelijst
              berichtSoortType="AX"
              caption="Partij"
              name={`producten[${selected}].product.partijCodeSelectie`}
              fieldSize="xl"
              keuzelijst={"MaatschappijType"}
              readonly={productSoort !== "orv" && productSoort !== "aov" && isDoorlopendInVoorstel}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                setFieldValue(`producten[${selected}].product.partijCodeSelectie`, e.target.value);
                if (e.target.selectedIndex !== -1) {
                  setFieldValue(
                    `producten[${selected}].product.partijNaam`,
                    e.target[e.target.selectedIndex].textContent
                  );
                }
              }}
            />
          )}
        </>
      )}
      {kenmerken.logoTonen &&
        producten[selected].partijCode &&
        situatie !== "huidig" &&
        producten[selected].partijCode !== partijOnafhankelijk && (
          //TODO: Goede location en name
          <LabeledAfbeelding
            caption="Partijlogo"
            name={`producten[${selected}].product.partij`}
            location={`${productenOrigin}/Logos/Logo/${producten[selected].partijCode}?subscription-key=${OcpApimSubscriptionKey}`}
          />
        )}
      {producten[selected].product.partijCodeSelectie === partijAnders && !isStarterslening && (
        <LabeledTextInput
          caption="Partijnaam"
          name={`producten[${selected}].product.partijNaam`}
          readonly={productSoort !== "orv" && productSoort !== "aov" && isDoorlopendInVoorstel}
        />
      )}
      {producten[selected].partijCode !== partijOnafhankelijk && isVoorstelHypotheek && !isStarterslening && (
        <>
          <LabeledTextInput caption="Productlabel" name={`producten[${selected}].labelNaam`} readonly />
        </>
      )}
      {kenmerken.productnaamTonen && (
        <LabeledTextInput
          caption="Productnaam"
          name={
            productSoort === "hypotheek"
              ? `producten[${selected}].hypotheekVorm.omschrijving`
              : `producten[${selected}].product.productNaam`
          }
          readonly={!kenmerken.productnaamEnabled || isStarterslening}
        />
      )}
      {productSoort === "overige" && (
        <LabeledTextInput caption="Omschrijving" name={`producten[${selected}].product.omschrijving`} />
      )}
      {productSoort === "hypotheek" && (
        <LabeledTextInput
          caption="Hypotheekvorm"
          name={`producten[${selected}].hypotheekVorm.aflossingsvorm`}
          readonly
        />
      )}
      {(isHuidigHypotheek || (productSoort === "hypotheek" && isDoorlopendInVoorstel)) && (
        <LabeledTextInput
          caption="Leningdeelnummer"
          name={`producten[${selected}].product.leningdeelnummer`}
          readonly={isDoorlopendInVoorstel}
        />
      )}
      {kenmerken.ingangsdatumTonen && (
        <LabeledDateInput
          caption="Ingangsdatum"
          name={`producten[${selected}].product.ingangsdatum`}
          readonly={
            (productSoort !== "orv" && productSoort !== "aov" && (isDoorlopendInVoorstel || isVoorstelHypotheek)) 
          }
          verplicht
        />
      )}
      {kenmerken.einddatumTonen && !isDoorlopendKredietZonderEinddatum(productSoort, product) && (
        <LabeledDateInput
          caption={productSoort === "krediet" && product.productCode === "02" ? "Theoretische einddatum" : "Einddatum"}
          name={`producten[${selected}].product.einddatum`}
          readonly={!kenmerken.einddatumEnabled || (productSoort !== "aov" && situatie === "voorstel")}
          fieldSize={"l"}
        />
      )}
      {kenmerken.looptijdTonen && !isDoorlopendKredietZonderLooptijd(productSoort, product) && (
        <LabeledJaarMaandInput
          caption="Looptijd"
          name={`producten[${selected}].product.looptijd`}
          readonly={productSoort !== "orv" && productSoort !== "aov" && isDoorlopendInVoorstel}
          jarenreadonly={!kenmerken.looptijdJaarEnabled}
          maandenreadonly={!kenmerken.looptijdMaandEnabled}
        />
      )}
      {kenmerken.uwBemiddelingTonen && (
        <LabeledBevestigingInput
          caption="Jouw bemiddeling"
          name={`producten[${selected}].product.uwBemiddeling`}
          tooltip={getProductenOverzichtTextResources("productUwBemiddeling")}
        />
      )}
    </>
  );
};

Product.displayName = "Product";

export default connect<ProductProps, ProductenState>(Product);

function isDoorlopendKredietZonderLooptijd(productSoort: ProductSoort, product: ProductDetailType): boolean {
  return (
    isDoorlopendKrediet(productSoort, product) &&
    product.product.looptijd.jaren === null &&
    product.product.looptijd.maanden === null
  );
}

function isDoorlopendKredietZonderEinddatum(productSoort: ProductSoort, product: ProductDetailType): boolean {
  return isDoorlopendKrediet(productSoort, product) && product.product.einddatum === null;
}

function isDoorlopendKrediet(productSoort: ProductSoort, product: ProductDetailType): boolean {
  return productSoort === "krediet" && product.soortProduct === SoortKredietProductOptions.DoorlopendKrediet;
}
